import React, { useEffect, useMemo, useState } from 'react';
// Reutilise le shell visuel de LancerMarcheModal (overlay + carte + form-group + modal-actions) : la
// modale de creation de chiffrage doit etre visuellement identique aux autres modales de lancement.
import '../../../composants/LancerMarcheModal/LancerMarcheModal.css';
import './ChiffrageCreateModal.css';
import Button from '../../../composants/Button/Button';
import Banner from '../../../composants/Banner/Banner';
import ProfilCardPrestataire from './ProfilCardPrestataire';
import chiffrageService from '../../../services/chiffrageService';
import type { Chiffrage, ChiffrageCouple } from '../chiffrageTypes';

// Modale de CREATION d'un chiffrage (mockup page 1, onglet catalogue). Le prestataire nomme
// l'operation, choisit un couple lot technique x lot geographique ou il est TITULAIRE (les couples
// fournis en prop sont deja restreints a la titularite via getCouples), et visualise la vignette des
// coordonnees qui seront transmises a l'acheteur avec ce chiffrage. La modale ne persiste rien : elle
// resout le couple et remonte { libelle, couple } au parent, qui pilote la creation reelle.

// Longueur max du nom : c'est la largeur de la colonne `chiffrage.libelle` (VARCHAR(255)), pas un
// choix d'affichage. Le module valide la meme borne (`max:255`).
const LIBELLE_MAX = 255;

interface ChiffrageCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  couples: ChiffrageCouple[];
  /** Appele APRES la creation effective : a la validation, le chiffrage est persiste tel quel, SANS
   *  aucun article (le presta compose son catalogue ensuite). Recoit le chiffrage cree. */
  onCreated: (chiffrage: Chiffrage) => void;
}

const ChiffrageCreateModal: React.FC<ChiffrageCreateModalProps> = ({
  isOpen,
  onClose,
  couples,
  onCreated,
}) => {
  // Nom de l'operation (libelle du chiffrage) + selection du couple, en chaine pour les <select> natifs.
  const [libelle, setLibelle] = useState('');
  const [tech, setTech] = useState('');
  const [geo, setGeo] = useState('');
  // Creation en cours / echec de creation (la modale reste ouverte pour laisser reessayer).
  const [saving, setSaving] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Formulaire vierge a chaque ouverture (meme comportement que LancerMarcheModal).
  useEffect(() => {
    if (isOpen) {
      setLibelle('');
      setTech('');
      setGeo('');
      setSaving(false);
      setCreateError(null);
    }
  }, [isOpen]);

  // Bloque le scroll de la page tant que la modale est ouverte.
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isOpen]);

  // Options du select "Lot technique" : distinct par id_lot_technique, label "code - nom".
  const techOptions = useMemo(() => {
    const seen = new Map<string, string>();
    couples.forEach((c) => {
      seen.set(String(c.id_lot_technique), `${c.lot_code} - ${c.lot_nom}`);
    });
    return Array.from(seen.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
  }, [couples]);

  // Options du select "Lot geographique" : DEPENDANT du lot technique choisi, label "id - departement".
  const geoOptions = useMemo(() => {
    if (!tech) return [] as { value: string; label: string }[];
    const seen = new Map<string, string>();
    couples
      .filter((c) => String(c.id_lot_technique) === tech)
      .forEach((c) => seen.set(String(c.id_departement), c.departement_nom));
    return Array.from(seen.entries())
      .map(([value, nom]) => ({ value, label: `${value} - ${nom}` }))
      .sort((a, b) => Number(a.value) - Number(b.value));
  }, [couples, tech]);

  // Couple resolu par la selection (tech + geo) : un presta est titulaire au plus une fois par couple.
  const selectedCouple = useMemo<ChiffrageCouple | null>(
    () =>
      couples.find(
        (c) => String(c.id_lot_technique) === tech && String(c.id_departement) === geo,
      ) ?? null,
    [couples, tech, geo],
  );

  // Valide tant que le nom est renseigne ET le couple resolu (titularite garantie par la prop couples).
  const canValidate = libelle.trim().length > 0 && selectedCouple != null;

  // Valider = CREER. Le chiffrage est persiste des maintenant avec `articles: []` : il existe en base
  // (et donc dans la liste) avant meme que le presta ait choisi le moindre article. Le back l'accepte
  // (`articles` est nullable) ; seule la generation du code exigera un chiffrage non vide.
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCouple || libelle.trim().length === 0 || saving) return;
    setSaving(true);
    setCreateError(null);
    try {
      const created = await chiffrageService.createChiffrage({
        id_lot_technique: selectedCouple.id_lot_technique,
        id_departement: selectedCouple.id_departement,
        rang: selectedCouple.rang,
        libelle: libelle.trim(),
        articles: [],
      });
      onCreated(created);
      onClose();
    } catch (err) {
      setCreateError(
        err instanceof Error ? err.message : "La création du chiffrage a échoué, veuillez réessayer.",
      );
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="lancer-marche-modal" onClick={(e) => e.stopPropagation()}>
        <h2>Nouveau chiffrage</h2>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="ccm-libelle">
              Nom de l'opération <span className="required">*</span>
            </label>
            {/* `maxLength` cale sur la colonne : `chiffrage.libelle` est un VARCHAR(255). Sans lui, un
                nom plus long partait jusqu'a Postgres, qui le rejetait en 22001 — le catch du module
                le rendait en 500 "Une erreur est survenue", sans jamais dire que le nom etait trop
                long. Le back valide la meme borne (max:255) : ceci est du confort de saisie, pas la
                garantie. */}
            <input
              id="ccm-libelle"
              type="text"
              value={libelle}
              onChange={(e) => setLibelle(e.target.value)}
              placeholder="Nom de l'opération"
              maxLength={LIBELLE_MAX}
            />
          </div>

          <div className="form-group">
            <label htmlFor="ccm-tech">
              Lot technique <span className="required">*</span>
            </label>
            <select
              id="ccm-tech"
              value={tech}
              onChange={(e) => {
                setTech(e.target.value);
                setGeo('');
              }}
            >
              <option value="">Sélectionnez un lot technique</option>
              {techOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="ccm-geo">
              Lot géographique <span className="required">*</span>
            </label>
            <select
              id="ccm-geo"
              value={geo}
              disabled={!tech}
              onChange={(e) => setGeo(e.target.value)}
            >
              <option value="">Sélectionnez un lot géographique</option>
              {geoOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {/* Vignette coordonnees : la carte de profil du prestataire (sans clic), telle qu'elle
              apparait a l'acheteur. Alimentee par le profil (useProfile) -> s'affiche sans dependre du
              choix des lots. La banniere d'information est posee AU-DESSUS de la carte. */}
          <div className="ccm-vignette">
            <Banner
              variant="info"
              className="ccm-vignette-banner"
              message="Pensez à mettre à jour votre profil si nécessaire."
            />
            <ProfilCardPrestataire />
          </div>

          {createError && (
            <Banner variant="danger" className="ccm-vignette-banner" message={createError} />
          )}

          <div className="modal-actions">
            <Button value="Annuler" variant="secondary" type="button" onClick={onClose} disabled={saving} />
            <Button
              value={saving ? 'Création...' : 'Valider'}
              variant="primary"
              type="submit"
              disabled={!canValidate || saving}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChiffrageCreateModal;
